import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type { BilanEcf, EcfDuDossier } from './useEcf';
import type { Avancement, BlocDuCours, LeconAReprendre } from './useLecteur';

/**
 * Espace apprenant : tout l'écran vient de `/espace-apprenant/tableau-bord`,
 * qui ne renvoie que le dossier de l'utilisateur connecté. Rien n'y est
 * complété par une valeur d'exemple ; ce que la plateforme ignore arrive
 * `null` et l'écran le dit.
 */

export type ModeDossier = 'CFA' | 'OF';
export type StatutPresence = 'ATTENDU' | 'PRESENT' | 'RETARD' | 'ABSENT';
export type StatutSeance = 'PREVUE' | 'EN_COURS' | 'CLOTUREE';
export type StatutPiece = 'ATTENDUE' | 'DEPOSEE' | 'VERIFIEE' | 'VALIDEE' | 'REFUSEE';

export interface SeanceApprenant {
  seanceId: string;
  presenceId: string;
  libelle: string;
  date: string;
  demiJournee: string;
  heureDebut: string;
  heureFin: string;
  lieu: string | null;
  type: string;
  formateur: string | null;
  statutSeance: StatutSeance;
  statutPresence: StatutPresence;
  statutTexte: string;
  statutTon: string;
  signeLe: string | null;
  emargeable: boolean;
}

export type FamilleDocumentApprenant = 'CONTRAT' | 'FORMATION';
export type EtatDocumentApprenant = 'A_SIGNER' | 'EN_SIGNATURE' | 'SIGNE' | 'DISPONIBLE';

/**
 * Un document tel que l'apprenant le lit. La règle de diffusion vit dans
 * l'API (`domain/documents-apprenant.ts`) : ce qui arrive ici lui est remis.
 */
export interface DocumentApprenant {
  id: string;
  type: string;
  intitule: string;
  famille: FamilleDocumentApprenant;
  contratId: string | null;
  etat: EtatDocumentApprenant;
  etatTexte: string;
  etatTon: string;
  /** Mise à disposition ; `null` pour un document seulement présenté à la signature. */
  recuLe: string | null;
  signeLe: string | null;
  demandeSignatureId: string | null;
  /** Mis à disposition et jamais ouvert depuis. */
  nouveau: boolean;
  /** Le fichier servi est l'exemplaire signé. */
  exemplaireSigne: boolean;
  telechargeable: boolean;
}

/**
 * Synthèse d'assiduité : la même que celle de l'équipe (`domain/assiduite.ts`).
 * Les heures suivies sont celles que le certificat de réalisation atteste.
 */
export interface SyntheseEmargement {
  convoquees: number;
  emargees: number;
  presents: number;
  retards: number;
  absences: number;
  absencesJustifiees: number;
  absencesNonJustifiees: number;
  /** Demi-journées pas encore pointées. */
  attendues: number;
  tauxPresence: number | null;
  signatures: number;
  heuresSuivies: number;
  /** Heures programmées des demi-journées pointées. */
  heuresPrevues: number;
}

export interface FichierPieceApprenant {
  id: string;
  nomFichier: string;
  tailleOctets: number;
  typeMime: string;
  deposeLe: string;
}

export interface PieceApprenant {
  id: string;
  type: string;
  libelle: string;
  obligatoire: boolean;
  statut: StatutPiece;
  statutTexte: string;
  statutTon: string;
  deposeeLe: string | null;
  motifRefus: string | null;
  /** Règle de dépôt du référentiel : nombre de fichiers, formats, poids max. */
  nombreAttendu: number;
  nombreMaximum: number;
  formatsAcceptes: string[];
  libelleFormats: string;
  tailleMaxOctets: number;
  fichiers: FichierPieceApprenant[];
}

export interface EcheanceApprenant {
  cle: string;
  libelle: string;
  date: string;
  joursRestants: number;
}

export interface TableauBordApprenant {
  dossier: {
    id: string;
    prenom: string;
    nom: string;
    mode: ModeDossier;
    statut: string;
    statutTexte: string;
    statutTon: string;
    site: string;
    formation: string | null;
    session: string | null;
    dateEntree: string | null;
    dateFinPrevue: string | null;
  };
  emargement: SyntheseEmargement;
  seancesDuJour: SeanceApprenant[];
  documents: DocumentApprenant[];
  pieces: PieceApprenant[];
  piecesAFournir: number;
  echeances: EcheanceApprenant[];
  /** La prochaine leçon à suivre dans les cours en ligne (TF-450). */
  reprise: RepriseFormation;
  /** Taux de complétion de la formation : leçons et quiz (TF-451) ; `null` quand les cours en ligne sont fermés. */
  completion: Avancement | null;
  /** Les devoirs à rendre les plus urgents, et combien il y en a en tout. */
  devoirs: { aRendre: DevoirApprenant[]; nombre: number };
  prochainsCours: ProchainsCours;
  /** Bilan des ECF ; `null` : pas d'ECF au programme du dossier. */
  ecf: BilanEcf | null;
}

/**
 * Le cours à reprendre : le dernier regardé, à sa leçon de reprise, sinon la
 * première leçon du premier cours publié. La règle est celle du lecteur.
 */
export interface RepriseFormation {
  /** Ce qui ferme les cours en ligne au dossier, ou `null`. */
  refus: string | null;
  formation: string | null;
  /** `null` : aucun cours publié n'a de leçon. */
  cours: {
    id: string;
    titre: string;
    auteur: string;
    nombreLecons: number;
    nombreModules: number;
    /** Bloc du programme que couvre le cours (TF-451) ; `null` : transversal. */
    bloc: BlocDuCours | null;
    /** Leçons terminées du cours (TF-451). */
    avancement: Avancement;
    /** `null` : le cours se commence. */
    regardeLe: string | null;
    lecon: LeconAReprendre & {
      rang: number;
      module: { numero: number; titre: string };
      dureeSecondes: number | null;
      video: boolean;
    };
  } | null;
}

export type GenreCours = 'PRESENTIEL' | 'A_DISTANCE' | 'CENTRE_FORMATION' | 'EPREUVE_ECRITE' | 'ECF';

/** Un prochain cours, lu dans le planning du calendrier. */
export interface CoursAVenir {
  cle: string;
  genre: GenreCours;
  genreLibelle: string;
  libelle: string;
  date: string;
  /** Jours civils réunionnais d'ici le cours : 0 aujourd'hui. */
  joursRestants: number;
  /** `null` : la programmation ne dit pas l'heure. */
  heureDebut: string | null;
  heureFin: string | null;
  lieu: string | null;
  formateur: string | null;
}

export interface ProchainsCours {
  cours: CoursAVenir[];
  /** Jours de cours d'aujourd'hui à dimanche. */
  joursCetteSemaine: number;
}

/**
 * Relu à chaque arrivée sur le tableau de bord et au retour sur l'onglet : la
 * leçon reprise, les devoirs et les prochains cours y sont toujours ceux du
 * moment (TF-450).
 */
export function useTableauBordApprenant() {
  return useQuery({
    queryKey: ['espace-apprenant', 'tableau-bord'],
    queryFn: () => appelApi<TableauBordApprenant>('/espace-apprenant/tableau-bord'),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });
}

/**
 * URL d'un document du dossier, servie par une route gardée. `consulter` l'ouvre
 * dans le navigateur au lieu de l'enregistrer : c'est la lecture avant signature.
 */
export function urlDocumentApprenant(documentId: string, consulter = false): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/espace-apprenant/documents/${documentId}/fichier${consulter ? '?consulter=1' : ''}`;
}

/** Taux de présence à la française, `—` quand rien n'est encore pointé. */
export function pourcentagePresence(taux: number | null): string {
  return taux === null ? '—' : `${String(taux).replace('.', ',')} %`;
}

/** Heures décimales à la française : « 132,5 h ». */
export function heures(valeur: number): string {
  return `${String(valeur).replace('.', ',')} h`;
}

// ---------------------------------------------------------------------------
// Mes documents, ma formation, mes attestations, mon calendrier
// ---------------------------------------------------------------------------

export interface InterlocuteurApprenant {
  role: string;
  /** Compte plateforme, quand il existe : sert à ouvrir le fil de messagerie. */
  utilisateurId: string | null;
  nom: string;
  initiales: string;
  email: string | null;
  telephone: string | null;
}

export interface SignatureAttendue {
  id: string;
  libelle: string;
  documentId: string | null;
  demandeeLe: string;
}

/** Un contrat de l'apprenant, avec les documents qui s'y rapportent. */
export interface ContratApprenant {
  id: string;
  employeur: string;
  dateDebut: string;
  dateFin: string;
  etatTexte: string;
  etatTon: string;
  rompuLe: string | null;
  documents: DocumentApprenant[];
}

export interface MesDocumentsApprenant {
  contrats: ContratApprenant[];
  /** Documents contractuels sans contrat : convention d'un parcours OF, ou plus ancienne. */
  documentsContractuels: DocumentApprenant[];
  documentsFormation: DocumentApprenant[];
  documentsRecus: number;
  nouveaux: number;
  pieces: PieceApprenant[];
  piecesAFournir: number;
  signaturesAttendues: SignatureAttendue[];
  emargement: SyntheseEmargement;
  dernieresDemiJournees: SeanceApprenant[];
  interlocuteurs: InterlocuteurApprenant[];
}

export interface ModuleApprenant {
  id: string;
  libelle: string;
  heures: number;
  ordre: number;
}

export interface MaFormationApprenant {
  formation: {
    id: string;
    code: string;
    titre: string;
    niveau: string;
    rncp: string;
    codeDiplome: string;
    heuresPrevues: number;
    modules: ModuleApprenant[];
  } | null;
  session: { id: string; libelle: string; dateDebut: string; dateFin: string } | null;
  mode: ModeDossier;
  dateEntree: string | null;
  dateFinPrevue: string | null;
  emargement: SyntheseEmargement;
  prochainesSeances: SeanceApprenant[];
  dernieresSeances: SeanceApprenant[];
  formateurs: string[];
  /** Ses ECF, bloc par bloc : résultat et observation de l'évaluateur. */
  ecf: EcfDuDossier;
  /** Progression dans les cours en ligne, bloc par bloc (TF-451). */
  progression: ProgressionFormation;
  /** Le cours à reprendre, et où. */
  reprise: RepriseFormation;
}

/** Un cours en ligne dans « Mes formations » (TF-451) : son taux et celui de chaque module. */
export interface CoursEnProgression {
  id: string;
  titre: string;
  auteur: string;
  avancement: Avancement;
  modules: { id: string; titre: string; avancement: Avancement }[];
  /** La leçon qu'ouvre le cours : sa reprise, sinon sa première leçon. */
  prochaineLecon: LeconAReprendre | null;
}

/** Un quiz du bloc (TF-451), ouvert par `/quiz/:id`. */
export interface QuizEnProgression {
  id: string;
  titre: string;
  /** Le cours qui le porte (TF-454) ; `null` pour un quiz sans cours. */
  cours: string | null;
  echeance: string;
  fait: boolean;
  /** Note sur 20 d'un quiz fait dont le score est connu, sinon `null`. */
  note: number | null;
}

export interface BlocEnProgression {
  /** Le bloc du programme (`formation.modules`). */
  id: string;
  /** `taux: null` : rien à faire en ligne pour ce bloc. */
  avancement: Avancement;
  cours: CoursEnProgression[];
  quiz: QuizEnProgression[];
}

/**
 * Progression dans les cours en ligne (TF-451) : leçons terminées — vidéo vue
 * jusqu'au bout, ou leçon à ressources ouverte — et quiz faits, bloc par bloc.
 * Tous les chiffres viennent de l'API.
 */
export interface ProgressionFormation {
  /** Ce qui ferme les cours en ligne au dossier, ou `null` ; rien n'est compté alors. */
  refus: string | null;
  formation: Avancement;
  /** Modules de cours terminés, sur ceux qui ont une leçon à suivre. */
  modules: { completes: number; total: number };
  quiz: Avancement & { scoreMoyen: number | null };
  blocs: BlocEnProgression[];
  transversal: { avancement: Avancement; cours: CoursEnProgression[] };
  derniereActivite: string | null;
}

export interface CertificatApprenant extends DocumentApprenant {
  /** Lien LinkedIn « Licences et certifications » prérempli ; `null` : ne s'ajoute pas au profil. */
  linkedin: string | null;
}

/** « Certificats & attestations » (TF-455). */
export interface MesCertificatsApprenant {
  certificats: CertificatApprenant[];
  mode: ModeDossier;
  beneficiaire: string;
  formation: { titre: string; rncp: string; niveau: string } | null;
  /** Référence du dossier imprimée sur les documents de l'organisme. */
  reference: string;
  organisme: string;
  dateEntree: string | null;
  dateFinPrevue: string | null;
  emargement: SyntheseEmargement;
  ecf: BilanEcf | null;
  /** Certificat de réalisation attendu et pas encore remis : ce que la page en dit. */
  enAttente: string | null;
}

export interface MonCalendrierApprenant {
  debut: string;
  fin: string;
  seances: SeanceApprenant[];
  echeances: EcheanceApprenant[];
}

export function useMesDocumentsApprenant(actif = true) {
  return useQuery({
    queryKey: ['espace-apprenant', 'documents'],
    queryFn: () => appelApi<MesDocumentsApprenant>('/espace-apprenant/documents'),
    enabled: actif,
  });
}

export type QualificationPresence =
  | 'ATTENDU'
  | 'PRESENT'
  | 'RETARD'
  | 'DEPART_ANTICIPE'
  | 'PRESENCE_PARTIELLE'
  | 'ABSENT'
  | 'ABSENCE_JUSTIFIEE';

/** Une demi-journée de l'apprenant, avec ses horaires réels. */
export interface LigneEmargementApprenant {
  presenceId: string;
  seanceId: string;
  jour: string;
  jourLisible: string;
  demiJournee: 'MATIN' | 'APRES_MIDI';
  demiJourneeLibelle: string;
  seance: string;
  formation: string | null;
  session: string | null;
  heureDebut: string;
  heureFin: string;
  heureArrivee: string | null;
  heureDepart: string | null;
  statut: StatutPresence;
  statutLibelle: string;
  statutTon: string;
  qualification: QualificationPresence;
  qualificationLibelle: string;
  qualificationTon: string;
  arriveeEffective: string | null;
  departEffectif: string | null;
  minutesPresence: number | null;
  justifiee: boolean;
  motifJustification: string | null;
  signeLe: string | null;
  /** Classe à distance : émargée d'un geste dans la salle, caméra allumée. */
  distance: boolean;
}

export interface MonEmargementApprenant {
  formation: string | null;
  session: string | null;
  synthese: SyntheseEmargement;
  cumul: {
    total: number;
    injustifiees: number;
    justifiees: number;
    seuil: number;
    seuilDepasse: boolean;
    margeAvantSeuil: number;
  };
  historique: LigneEmargementApprenant[];
  prochaines: LigneEmargementApprenant[];
}

/** Relevé de présence en PDF, composé à la demande par une route gardée. */
export function urlRelevePresence(): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/espace-apprenant/emargement/releve.pdf`;
}

export function useMonEmargement() {
  return useQuery({
    queryKey: ['espace-apprenant', 'emargement'],
    queryFn: () => appelApi<MonEmargementApprenant>('/espace-apprenant/emargement'),
  });
}

export function useMaFormationApprenant() {
  return useQuery({
    queryKey: ['espace-apprenant', 'formation'],
    queryFn: () => appelApi<MaFormationApprenant>('/espace-apprenant/formation'),
  });
}

export function useMesCertificatsApprenant() {
  return useQuery({
    queryKey: ['espace-apprenant', 'certificats'],
    queryFn: () => appelApi<MesCertificatsApprenant>('/espace-apprenant/certificats'),
  });
}

/*
 * Le calendrier de l'apprenant ne passe plus par ici : l'écran `/calendar`
 * interroge `/calendrier/planning`, qui sert tous les profils depuis une seule
 * route et décide du périmètre côté serveur. Garder un second chemin vers les
 * mêmes séances, réservé à un seul profil, c'était se condamner à corriger dans
 * celui qui n'est pas lu. La route `/espace-apprenant/calendrier` reste en
 * place côté API, sans appelant dans le front.
 */

export type NiveauFrise = 'info' | 'warning' | 'danger' | 'mint';
/**
 * « Refus » est réservé à ce que l'apprenant a refusé lui-même ; une pièce
 * refusée ou une candidature non retenue se range sous « Dossier ».
 */
export type CategorieFrise =
  | 'Accompagnement'
  | 'Handicap'
  | 'Aménagement'
  | 'Refus'
  | 'Dossier'
  | 'Alerte';

export interface EvenementFrise {
  date: string;
  titre: string;
  detail: string;
  niveau: NiveauFrise;
  categorie: CategorieFrise;
}

/** Frise du dossier de l'apprenant connecté, sans les alertes rédigées pour les pôles. */
export function useFriseApprenant() {
  return useQuery({
    queryKey: ['espace-apprenant', 'frise'],
    queryFn: () => appelApi<EvenementFrise[]>('/espace-apprenant/frise'),
  });
}

// ---------------------------------------------------------------------------
// Devoirs (écran « assignments »)
// ---------------------------------------------------------------------------

export type TypeDevoir = 'PROJET' | 'QUIZ' | 'RAPPORT' | 'AUTRE';

/** À rendre / rendu / corrigé (TF-453) ; « corrigé » arrive avec la correction publiée (TF-457). */
export type StatutDevoir = 'A_RENDRE' | 'RENDU' | 'CORRIGE';

export interface DepotDevoirApprenant {
  id: string;
  numero: number;
  deposeLe: string;
  enRetard: boolean;
  fichier: { nom: string; tailleOctets: number; typeMime: string } | null;
  liens: string[];
}

export interface DevoirApprenant {
  id: string;
  titre: string;
  description: string | null;
  type: TypeDevoir;
  cours: string;
  formateur: string | null;
  echeance: string;
  joursRestants: number;
  niveau: 'danger' | 'warning' | 'info';
  statut: StatutDevoir;
  /** Rendu après l'échéance, ou toujours à rendre une fois elle passée. */
  enRetard: boolean;
  /** Du plus récent au plus ancien : le premier est celui que le formateur corrige. */
  depots: DepotDevoirApprenant[];
  /** Ce qui empêche un dépôt (quiz, dossier hors formation, maximum atteint…), ou `null`. */
  refusDepot: string | null;
  /**
   * Un quiz : questions, durée, commencé, note sur 20 une fois fait — publiée,
   * ou automatique et provisoire tant que `correction` est vide ; `null` pour
   * un devoir à rendre.
   */
  quiz: { questions: number; dureeMinutes: number; commence: boolean; note: number | null } | null;
  /** La correction publiée par le formateur (TF-457), `null` avant. */
  correction: CorrectionApprenant | null;
}

/** La correction publiée par le formateur (TF-457). */
export interface CorrectionApprenant {
  /** Note sur 20, au dixième. */
  note: number;
  commentaire: string | null;
  correcteur: string | null;
  publieeLe: string;
  /** Rectifiée depuis, sur validation de la direction. */
  rectifieeLe: string | null;
  /** Numéro du dépôt corrigé ; `null` pour un quiz. */
  depot: number | null;
}

export interface LimitesDepotDevoir {
  extensions: string[];
  libelleFormats: string;
  tailleMaxOctets: number;
  libelleTailleMax: string;
  liensMax: number;
  depotsMax: number;
}

export interface DevoirsApprenant {
  aRendre: DevoirApprenant[];
  tous: DevoirApprenant[];
  /** Moyenne sur 20 des corrections publiées, devoirs et quiz ; `null` sans correction. */
  moyenne: number | null;
  limites: LimitesDepotDevoir;
}

/** Devoirs demandés au dossier de l'apprenant connecté, leur statut et leurs dépôts. */
export function useDevoirsApprenant(actif = true) {
  return useQuery({
    queryKey: ['espace-apprenant', 'devoirs'],
    queryFn: () => appelApi<DevoirsApprenant>('/espace-apprenant/devoirs'),
    enabled: actif,
  });
}

/** Rend un devoir : un fichier PDF ou ZIP et/ou des liens. L'API répond par la liste à jour. */
export function useDeposerDevoir() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ devoirId, fichier, liens }: { devoirId: string; fichier: File | null; liens: string[] }) => {
      const formulaire = new FormData();
      for (const lien of liens) formulaire.append('liens', lien);
      if (fichier) formulaire.append('fichier', fichier);
      return appelApi<DevoirsApprenant>(`/espace-apprenant/devoirs/${encodeURIComponent(devoirId)}/depots`, {
        methode: 'POST',
        formulaire,
      });
    },
    onSuccess: (devoirs) => {
      client.setQueryData(['espace-apprenant', 'devoirs'], devoirs);
      void client.invalidateQueries({ queryKey: ['espace-apprenant', 'tableau-bord'] });
    },
  });
}

/** Le fichier d'un de ses dépôts, servi par une route gardée. */
export function urlFichierDepotDevoir(depotId: string): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/espace-apprenant/devoirs/depots/${encodeURIComponent(depotId)}/fichier`;
}

// ---------------------------------------------------------------------------
// Quiz d'un devoir (écran `quiz`)
// ---------------------------------------------------------------------------

export interface OptionQuizApprenant {
  id: string;
  lettre: string;
  texte: string;
  /** `null` tant que la question n'a pas été jouée et que le quiz n'est pas terminé. */
  correcte: boolean | null;
}

export interface QuestionQuizApprenant {
  id: string;
  ordre: number;
  enonce: string;
  options: OptionQuizApprenant[];
  reponse: { optionId: string | null; correcte: boolean } | null;
  /** Montrée avec la bonne réponse, une fois la question jouée. */
  explication: string | null;
}

export interface TentativeQuizApprenant {
  demarreeLe: string;
  termineeLe: string | null;
  tempsRestantSecondes: number;
}

export interface QuizApprenant {
  devoirId: string;
  titre: string;
  consigne: string | null;
  /** « Cours · Module », ou « Formation · Bloc » pour un quiz sans cours. */
  contexte: string;
  echeance: string;
  enRetard: boolean;
  dureeMinutes: number;
  nombreQuestions: number;
  /** `null` : pas encore commencé ; les questions ne sortent qu'après. */
  tentative: TentativeQuizApprenant | null;
  /** Ce qui empêche de commencer, ou `null`. */
  refusDebut: string | null;
  questions: QuestionQuizApprenant[];
  /** Le score : sa note automatique, provisoire jusqu'à la correction publiée. */
  resultat: { bonnes: number; total: number; note: number; pourcentage: number } | null;
  /** La correction publiée par le formateur : sa note remplace la note automatique. */
  correction: CorrectionApprenant | null;
}

function cleQuiz(devoirId: string) {
  return ['espace-apprenant', 'quiz', devoirId] as const;
}

/** Le quiz d'un devoir : son accueil, ou la tentative en cours ; l'ouvrir ne lance pas le temps. */
export function useQuizApprenant(devoirId: string) {
  return useQuery({
    queryKey: cleQuiz(devoirId),
    queryFn: () => appelApi<QuizApprenant>(`/espace-apprenant/devoirs/${encodeURIComponent(devoirId)}/quiz`),
    enabled: Boolean(devoirId),
  });
}

/**
 * Le quiz a changé : l'écran le remplace ; une fois clos, les devoirs, le
 * tableau de bord, « Mes formations » et le lecteur relisent son résultat.
 */
function apresQuiz(client: QueryClient, devoirId: string, quiz: QuizApprenant): void {
  client.setQueryData(cleQuiz(devoirId), quiz);
  if (quiz.resultat) {
    for (const cle of [['espace-apprenant', 'devoirs'], ['espace-apprenant', 'tableau-bord'], ['espace-apprenant', 'formation'], ['lecteur']]) {
      void client.invalidateQueries({ queryKey: cle });
    }
  }
}

function geste(devoirId: string, suite: string, corps?: unknown) {
  return appelApi<QuizApprenant>(`/espace-apprenant/devoirs/${encodeURIComponent(devoirId)}/quiz/${suite}`, {
    methode: 'POST',
    ...(corps === undefined ? {} : { corps }),
  });
}

/** « Commencer » : crée la tentative et lance le temps. */
export function useCommencerQuiz(devoirId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => geste(devoirId, 'tentative'),
    onSuccess: (quiz) => apresQuiz(client, devoirId, quiz),
  });
}

/** Répond à une question du quiz ; `optionId` à `null` pour la passer. */
export function useRepondreQuizQuestion(devoirId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { questionId: string; optionId: string | null }) => geste(devoirId, 'reponses', variables),
    onSuccess: (quiz) => apresQuiz(client, devoirId, quiz),
    // Refusée (temps écoulé, question déjà jouée ailleurs) : l'écran relit le quiz.
    onError: () => void client.invalidateQueries({ queryKey: cleQuiz(devoirId) }),
  });
}

/** Clôt le quiz, à la demande de l'apprenant ou quand le temps est écoulé. */
export function useTerminerQuiz(devoirId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => geste(devoirId, 'terminer'),
    onSuccess: (quiz) => apresQuiz(client, devoirId, quiz),
  });
}
